import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Sparkles, Shield } from 'lucide-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';

export default function AltText() {
  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-4">Alt-Text Generation</h1>
        <p className="text-lg text-gray-600 mb-4">
          Automatically generate accessible alt-text for uploaded images using AI
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <Shield className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>TLDR:</strong> Add <code>alt-text</code> to your Simple File Upload component and listen for the <code>altTextGenerated</code> event to receive AI-generated descriptions for uploaded images.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5" />
          <div className="text-sm text-blue-700">
            Alt-text generation uses OpenAI's vision API to analyze images and provide descriptive text for accessibility.
            This feature helps make your application more accessible to users with screen readers and is great for SEO. <Sparkles className="w-4 h-4 text-yellow-500 inline-block ml-1" />
          </div>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Enabling Alt-Text Generation</CardTitle>
          <CardDescription>
            Add the alt-text attribute to your Simple File Upload component
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            To enable automatic alt-text generation, simply add the <code className="bg-muted px-2 py-1 rounded">alt-text</code> attribute
            to your Simple File Upload web component:
          </p>
          <SyntaxHighlighter language="xml" style={atomOneDark}>
{`<simple-file-upload
  public-key="YourPublicKey"
  alt-text
></simple-file-upload>`}
          </SyntaxHighlighter>
          
          <div className="mt-6">
            <h4 className="font-semibold mb-2">Optional Configuration</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <code className="bg-muted px-2 py-1 rounded">alt-text-delay="2000"</code> - Initial delay before checking for alt-text (default: 2000ms)
              </li>
              <li>
                <code className="bg-muted px-2 py-1 rounded">alt-text-max-attempts="6"</code> - Maximum attempts to check for alt-text (default: 6)
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Listening for Alt-Text Events</CardTitle>
          <CardDescription>
            Capture generated alt-text when it becomes available
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            When alt-text generation completes, the component fires an <code className="bg-muted px-2 py-1 rounded">altTextGenerated</code> event
            with the following data:
          </p>
          
          <div className="bg-muted p-4 rounded mb-6">
            <pre className="text-sm">
{`{
  fileId: "unique-file-id",
  altText: "Generated alt-text description",
  cdnUrl: "https://cdn.simplefileupload.com/..."
}`}
            </pre>
          </div>
          
          <p className="mb-4">
            The alt-text event is separate from the change events (like fileUploaded) because vision processing take a few seconds. 
            You'll need to listen for both events and match the alt-text to the correct file ID when handling multiple uploads (don't worry, it's easy)
          </p>
          
          <p className="mb-4 font-semibold">Examples:</p>

          <div className="space-y-6">
            <div>
              <h4 className="font-semibold mb-2">Vanilla JavaScript</h4>
              <SyntaxHighlighter language="javascript" style={atomOneDark}>
{`// Get reference to the uploader component
const uploader = document.querySelector('simple-file-upload');
// Optional image container so you can see how to create and display images immediately
const imageContainer = document.getElementById('image-container');

// Listen for file uploads
uploader.addEventListener('change', (event) => {
  // Only process if files were uploaded (not removed)
  if (event.detail.action === 'fileUploaded') {
    const { allFiles } = event.detail;
    
    // Process each uploaded file
    allFiles.forEach(file => {
      // Create image element with placeholder alt text
      const img = document.createElement('img');
      img.src = file.cdnUrl;
      img.alt = 'Processing image...';
      img.setAttribute('data-file-id', file.id);
      img.className = 'uploaded-image';
      
      imageContainer.appendChild(img);
    });
  }
});

// Listen for alt-text generation
uploader.addEventListener('altTextGenerated', (event) => {
  const { fileId, altText } = event.detail;
  
  // Find the image and update its alt text
  const img = document.querySelector(\`img[data-file-id="\${fileId}"]\`);
  if (img) {
    img.alt = altText;
    console.log(\`Alt text for \${fileId}: \${altText}\`);
  }
});`}
              </SyntaxHighlighter>
            </div>

            <div>
              <h4 className="font-semibold mb-2">React</h4>
              <SyntaxHighlighter language="javascript" style={atomOneDark}>
{`import { useState, useRef } from 'react';

function ImageUploader() {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileUploadRef = useRef(null);

  const handleFileChange = (event) => {
    // Map the files to include necessary properties
    if (event.allFiles) {
      const mappedFiles = event.allFiles.map((file) => ({
        id: file.id || file.name,
        name: file.name,
        url: file.cdnUrl || file.preview,
        cdnUrl: file.cdnUrl,
        altText: 'Processing image...'
      }));
      setUploadedFiles(mappedFiles);
    }
  };

  const handleAltTextGenerated = (event) => {
    // Update specific image with alt-text
    console.log('Alt text generated - File ID:', event.fileId, 'Alt Text:', event.altText);
    setUploadedFiles(prev => prev.map(img =>
      img.id === event.fileId
        ? { ...img, altText: event.altText }
        : img
    ));
  };

  return (
    <>
      <simple-file-upload
        ref={fileUploadRef}
        public-key="YourPublicKey"
        onChange={handleFileChange}
        onAltTextGenerated={handleAltTextGenerated}
        multiple={true}
        accept="image/*"
        alt-text
      />
      
      <div className="image-grid">
        {uploadedFiles.map(file => (
          <img 
            key={file.id}
            src={file.url}
            alt={file.altText}
            className="uploaded-image"
          />
        ))}
      </div>
    </>
  );
}`}
              </SyntaxHighlighter>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Matching Alt-Text to Files</CardTitle>
          <CardDescription>
            Associate generated alt-text with uploaded files
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            To properly match alt-text with uploaded files, you'll need to track the file IDs from the upload event:
          </p>
          
          <SyntaxHighlighter language="javascript" style={atomOneDark}>
{`// Track uploaded files
const uploadedFiles = new Map();

// Listen for upload completion
document.addEventListener('simplefileupload:complete', (event) => {
  const files = event.detail.files;
  
  files.forEach(file => {
    // Store file data indexed by fileId
    uploadedFiles.set(file.fileId, {
      fileId: file.fileId,
      cdnUrl: file.cdnUrl,
      filename: file.filename,
      altText: null // Will be populated when available
    });
  });
});

// Listen for alt-text generation
document.addEventListener('altTextGenerated', (event) => {
  const { fileId, altText } = event.detail;
  
  // Update the file record with alt-text
  const file = uploadedFiles.get(fileId);
  if (file) {
    file.altText = altText;
    
    // Update your database or UI
    updateFileRecord(file);
  }
});`}
          </SyntaxHighlighter>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Using Alt-Text in Your Application</CardTitle>
          <CardDescription>
            Display images with proper accessibility attributes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4">
            Once you have the alt-text, use it in your image tags for better accessibility:
          </p>
          
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold mb-2">HTML</h4>
              <SyntaxHighlighter language="html" style={atomOneDark}>
{`<!-- Image with alt-text -->
<img 
  src="https://cdn.simplefileupload.com/your-image.jpg"
  alt="A golden retriever playing fetch in a sunny park"
  loading="lazy"
/>

<!-- Fallback while alt-text is generating -->
<img 
  src="https://cdn.simplefileupload.com/your-image.jpg"
  alt="Image is being processed for accessibility"
  data-file-id="unique-file-id"
/>`}
              </SyntaxHighlighter>
            </div>

            <div>
              <h4 className="font-semibold mb-2">React Component</h4>
              <SyntaxHighlighter language="javascript" style={atomOneDark}>
{`function AccessibleImage({ file }) {
  const [altText, setAltText] = useState(file.altText);
  
  useEffect(() => {
    if (!altText) {
      // Check if alt-text is available in your database
      fetchAltText(file.fileId).then(setAltText);
    }
  }, [file.fileId, altText]);
  
  return (
    <img 
      src={file.cdnUrl}
      alt={altText || \`\${file.filename} - processing accessibility text\`}
      className="w-full h-auto"
    />
  );
}`}
              </SyntaxHighlighter>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Best Practices</CardTitle>
          <CardDescription>
            Tips for implementing alt-text in your application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              <div>
                <strong>Store alt-text permanently:</strong> Save generated alt-text in your database to avoid regenerating it
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              <div>
                <strong>Provide fallbacks:</strong> Use descriptive placeholder text while alt-text is being generated
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              <div>
                <strong>Handle errors gracefully:</strong> If alt-text generation fails, provide a meaningful fallback
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-primary mr-2">•</span>
              <div>
                <strong>Consider performance:</strong> Alt-text generation happens asynchronously, so your UI should handle the delay
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
